"use client"

import { ampli } from "@/ampli"
import { Button, Text } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import { JobApplicationStatus, JobInvitationStatus, JobPostingStatus } from "@/gql/graphql"
import type { NovaSession } from "@/types"
import { ProLockIcon } from "@/icons/outline"
import { decodeGraphqlNodeId } from "@/tracking"
import clsx from "clsx"
import Link from "next/link"
import { FC, useEffect, useState } from "react"
import { JobDetailsFooterAcceptOrDeclineInviteCTAs } from "./JobDetailsFooterAcceptOrDeclineInviteCTAs"
import { JobsDetailAuthenticatedFooterBanner } from "./JobsDetailsAuthenticatedTopBanner/JobsDetailAuthenticatedFooterBanner"

const AuthenticatedCreativeJobDetailsFooterFragment = graphql(`
  fragment AuthenticatedCreativeJobDetailsFooter on JobPosting {
    __typename
    id
    payString
    employmentTypeDisplay
    position {
      id
    }

    owner {
      id
    }
    viewerRedactionReason
    acceptingApplications
    status
    ...JobsDetailAuthenticatedFooterBannerJobPosting

    activeInteractionForCreative(creativeId: $creativeId) {
      ... on JobApplication {
        id
        applicationStatus: status
        invitation {
          id
          status
          acceptedAt
        }
        chat {
          id
        }

        ...JobsDetailAuthenticatedFooterBannerJobApplication
      }
      ... on JobInvitation {
        id
        invitationStatus: status
        createdAt
        message
        acceptedAt

        ...JobsDetailAuthenticatedFooterBannerJobInvitation
      }
    }
  }
`)

const RightElementProLockIcon = () => <ProLockIcon />

interface Props {
  jobPosting: FragmentType<typeof AuthenticatedCreativeJobDetailsFooterFragment> | undefined | null

  onApply: ({ jobPostingId, positionId }: { jobPostingId: string; positionId: string }) => void
  onViewApplication: ({ jobPostingId, positionId }: { jobPostingId: string; positionId: string }) => void
  serverSession: NovaSession
  openSubscriptionCheckoutModal: (jobId: string) => void
}

export const AuthenticatedCreativeJobDetailsFooter: FC<Props> = ({
  onApply,
  serverSession,
  onViewApplication,
  openSubscriptionCheckoutModal,
  ...props
}) => {
  const jobPosting = useFragment(AuthenticatedCreativeJobDetailsFooterFragment, props.jobPosting)

  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 900)

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 900)
    }
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const jobApplication =
    jobPosting?.activeInteractionForCreative?.__typename === "JobApplication"
      ? jobPosting?.activeInteractionForCreative
      : null

  const jobInvitation =
    jobPosting?.activeInteractionForCreative?.__typename === "JobInvitation"
      ? jobPosting?.activeInteractionForCreative
      : null

  const canApply = jobPosting?.viewerRedactionReason === null

  const hasActiveApplication = jobApplication?.applicationStatus === JobApplicationStatus.Submitted
  const hasRecievedInvitation = jobInvitation?.invitationStatus === JobInvitationStatus.Sent
  const isMyJobPosting = jobPosting?.owner?.id === serverSession?.user?.creative?.id
  const isCreativeJobPosting = jobPosting?.__typename === "CreativeJobPosting"
  const isMyJobPostingAndIncorrectAccount = jobPosting?.__typename === "ClientJobPosting" && isMyJobPosting

  const handleOnApply = () => {
    ampli.applyToJobStart({
      job_type: jobPosting?.__typename === "ClientJobPosting" ? "client" : "creative",
      job_id: decodeGraphqlNodeId(jobPosting?.id).numericalId || "",
    })

    if (canApply) {
      onApply({ jobPostingId: jobPosting?.id!, positionId: jobPosting?.position?.id! })
    } else {
      openSubscriptionCheckoutModal(jobPosting?.id!)
    }
  }

  const applyBlocked = !jobPosting?.acceptingApplications || jobPosting.status !== JobPostingStatus.Published

  return (
    <div
      className={clsx(
        "flex w-full justify-center rounded-tl-md rounded-tr-md bg-bg-1 md:rounded-tl-none md:rounded-tr-none"
      )}>
      <div className="relative flex w-full flex-col">
        <JobsDetailAuthenticatedFooterBanner
          jobApplication={jobApplication}
          jobInvitation={jobInvitation}
          isCreativeJobPosting={isCreativeJobPosting}
          jobPosting={jobPosting}
        />

        <div className="z-10 flex w-full flex-row items-center justify-between">
          <div className="flex flex-col gap-y-1 bg-bg-1">
            <Text variant="h5">{jobPosting?.payString}</Text>
            <Text variant="label" className="text-fg-2">
              <span className="hidden sm:inline">Type: </span>
              {jobPosting?.employmentTypeDisplay}
            </Text>
          </div>

          {hasRecievedInvitation ? (
            <JobDetailsFooterAcceptOrDeclineInviteCTAs
              invitationId={jobInvitation?.id}
              onApply={handleOnApply}
              serverSession={serverSession}
            />
          ) : hasActiveApplication ? (
            <Button
              size="large"
              onClick={() =>
                onViewApplication({ jobPostingId: jobPosting?.id!, positionId: jobPosting?.position?.id! })
              }>
              View application
            </Button>
          ) : isMyJobPostingAndIncorrectAccount ? (
            <></>
          ) : isMyJobPosting ? (
            <Link href={`/hire/${jobPosting?.id}`}>
              <Button variant="tertiary" className="md:hidden">
                Manage job
              </Button>
              <Button size="large" variant="tertiary" className="hidden md:block">
                Manage job
              </Button>
            </Link>
          ) : (
            <Button
              RightElement={jobPosting?.viewerRedactionReason !== null ? RightElementProLockIcon : null}
              disabled={applyBlocked}
              onClick={handleOnApply}
              size={isMobile ? "medium" : "large"}>
              Apply
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
